import React, { useState } from 'react';
import { Input, Button, Modal, Spin, Checkbox, InputNumber, Table } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { useDeptStaffingForm } from './hooks/useDeptStaffingForm';

const { TextArea } = Input;

const P9106Form: React.FC = () => {
  const {
    mode, isEditing, loading,
    records, current, currentIdx,
    deptCode, setDeptCode,
    deptName, setDeptName,
    category, setCategory,
    manager, setManager,
    managerName, setManagerName,
    extension, setExtension,
    isOldCode, setIsOldCode,
    vicePresident, setVicePresident,
    vpName, setVpName,
    remark, setRemark,
    deptGroup, setDeptGroup,
    details,
    totalStaffing, totalActual,
    navFirst, navPrev, navNext, navLast,
    startAdd, startEdit, cancel, save,
    doDelete, quickSearch,
    addDetail, removeDetail, updateDetail,
  } = useDeptStaffingForm();

  const [searchText, setSearchText] = useState('');
  const [newTitleCode, setNewTitleCode] = useState('');

  const handleDelete = () => {
    if (!current) return;
    Modal.confirm({
      title: '確認刪除',
      content: `確定要刪除部門「${current.f106_01} ${current.f106_11}」？`,
      okText: '刪除',
      okType: 'danger',
      cancelText: '取消',
      onOk: doDelete,
    });
  };

  const handleQuickSearch = () => {
    if (searchText.trim()) quickSearch(searchText.trim());
  };

  const handleAddDetail = () => {
    if (!newTitleCode.trim()) return;
    addDetail(newTitleCode.trim());
    setNewTitleCode('');
  };

  const modeLabel = mode === 'add' ? '新增' : mode === 'edit' ? '修改' : '瀏覽';

  const detailColumns = [
    {
      title: '職稱代碼',
      dataIndex: 'f906_02',
      key: 'f906_02',
      width: 120,
    },
    {
      title: '編制人數',
      dataIndex: 'f906_11',
      key: 'f906_11',
      width: 120,
      render: (val: number, _: any, idx: number) =>
        isEditing ? (
          <InputNumber
            value={val}
            min={0}
            onChange={v => updateDetail(idx, 'f906_11', v || 0)}
            size="small"
            style={{ width: 80 }}
          />
        ) : val,
    },
    {
      title: '實際人數',
      dataIndex: 'f906_12',
      key: 'f906_12',
      width: 120,
    },
    ...(isEditing
      ? [{
          title: '操作',
          key: 'action',
          width: 80,
          render: (_: any, __: any, idx: number) => (
            <Button
              type="link"
              danger
              size="small"
              icon={<DeleteOutlined />}
              onClick={() => removeDetail(idx)}
            />
          ),
        }]
      : []),
  ];

  return (
    <Spin spinning={loading}>
      <div className="max-w-4xl mx-auto p-6">
        {/* Title */}
        <h2 className="text-xl font-bold mb-4">
          部門編制設定 (pw9106f1)
          <span className="ml-3 text-sm font-normal text-gray-500">
            [{modeLabel}] {records.length > 0 ? `${currentIdx + 1} / ${records.length}` : '無資料'}
          </span>
        </h2>

        {/* Quick Search */}
        <div className="flex items-center gap-2 mb-4">
          <Input
            placeholder="輸入部門代碼快速查詢"
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            onPressEnter={handleQuickSearch}
            disabled={isEditing}
            style={{ width: 250 }}
            maxLength={4}
          />
          <Button onClick={handleQuickSearch} disabled={isEditing}>查詢</Button>
        </div>

        {/* Navigation */}
        <div className="flex items-center gap-1 mb-4">
          <Button size="small" onClick={navFirst} disabled={isEditing || records.length === 0}>|&lt;</Button>
          <Button size="small" onClick={navPrev} disabled={isEditing || currentIdx <= 0}>&lt;</Button>
          <Button size="small" onClick={navNext} disabled={isEditing || currentIdx >= records.length - 1}>&gt;</Button>
          <Button size="small" onClick={navLast} disabled={isEditing || records.length === 0}>&gt;|</Button>
        </div>

        {/* Master Form */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">部門代碼 (F106_01)</label>
            <Input
              value={deptCode}
              onChange={e => setDeptCode(e.target.value)}
              maxLength={4}
              disabled={mode !== 'add'}
              style={{ width: '100%' }}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">部門名稱 (F106_11)</label>
            <Input
              value={deptName}
              onChange={e => setDeptName(e.target.value)}
              maxLength={40}
              disabled={!isEditing}
              style={{ width: '100%' }}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">單位類別 (F106_02)</label>
            <InputNumber
              value={category}
              onChange={v => setCategory(v || 0)}
              min={0}
              disabled={!isEditing}
              style={{ width: '100%' }}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">分機 (F106_13)</label>
            <Input
              value={extension}
              onChange={e => setExtension(e.target.value)}
              disabled={!isEditing}
              style={{ width: '100%' }}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">部門主管 (F106_12)</label>
            <div className="flex gap-2">
              <Input
                value={manager}
                onChange={e => setManager(e.target.value)}
                maxLength={8}
                disabled={!isEditing}
                style={{ width: 120 }}
              />
              <Input
                value={managerName}
                onChange={e => setManagerName(e.target.value)}
                disabled={!isEditing}
                style={{ flex: 1 }}
                placeholder="姓名"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">副總 (F106_17)</label>
            <div className="flex gap-2">
              <Input
                value={vicePresident}
                onChange={e => setVicePresident(e.target.value)}
                maxLength={8}
                disabled={!isEditing}
                style={{ width: 120 }}
              />
              <Input
                value={vpName}
                onChange={e => setVpName(e.target.value)}
                disabled={!isEditing}
                style={{ flex: 1 }}
                placeholder="姓名"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">部門群組 (F106_24)</label>
            <Input
              value={deptGroup}
              onChange={e => setDeptGroup(e.target.value)}
              maxLength={4}
              disabled={!isEditing}
              style={{ width: '100%' }}
            />
          </div>
          <div className="flex items-end">
            <Checkbox
              checked={isOldCode}
              onChange={e => setIsOldCode(e.target.checked)}
              disabled={!isEditing}
            >
              舊代號 (F106_16)
            </Checkbox>
          </div>
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">備註 (F106_23)</label>
            <TextArea
              value={remark}
              onChange={e => setRemark(e.target.value)}
              rows={2}
              disabled={!isEditing}
              style={{ width: '100%' }}
            />
          </div>
        </div>

        {/* Staffing Totals */}
        <div className="flex gap-6 mb-4 text-sm">
          <span>編制總人數: <strong>{totalStaffing}</strong></span>
          <span>實際總人數: <strong>{totalActual}</strong></span>
        </div>

        {/* Detail Add Row */}
        {isEditing && (
          <div className="flex items-center gap-2 mb-2">
            <Input
              placeholder="職稱代碼"
              value={newTitleCode}
              onChange={e => setNewTitleCode(e.target.value)}
              maxLength={4}
              style={{ width: 150 }}
              onPressEnter={handleAddDetail}
            />
            <Button onClick={handleAddDetail}>新增明細</Button>
          </div>
        )}

        {/* Details Table */}
        <Table
          dataSource={details}
          columns={detailColumns}
          rowKey="f906_02"
          pagination={false}
          size="small"
          bordered
          className="mb-6"
        />

        {/* Audit Fields */}
        {current && mode === 'browse' && (
          <div className="text-xs text-gray-400 mb-6 space-y-1">
            <div>建立人員: {current.f106_91} | 建立日期: {current.f106_92 || '-'}</div>
            <div>修改人員: {current.f106_93}</div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center gap-2">
          {!isEditing ? (
            <>
              <Button type="primary" onClick={startAdd}>新增</Button>
              <Button onClick={startEdit} disabled={!current}>修改</Button>
              <Button danger onClick={handleDelete} disabled={!current}>刪除</Button>
            </>
          ) : (
            <>
              <Button type="primary" onClick={save}>儲存</Button>
              <Button onClick={cancel}>取消</Button>
            </>
          )}
        </div>
      </div>
    </Spin>
  );
};

export default P9106Form;
